/**
 * Read shapes for the ALREADY-REAL, generic `GET /background-jobs`(`/{id}`) surface (docs/
 * api-spec.md §22, IB-39/ADR-0033) — `module.job`'s unified async-operation read-model over
 * `IngestionRun`/`BulkOperation`/other `JobKind` consumers. Backs the `/data/import` screen's
 * "Recent activity" section; no backend change was needed for this endpoint.
 *
 * <p><b>Wire-format note (verified against the backend's plain-record serialization, no custom
 * Jackson enum config in this tree):</b> {@code kind} serializes as the raw Java enum name —
 * upper snake, e.g. {@code "INGESTION_RUN"} — while {@code status}/{@code phase} are the service's
 * own lower-snake wire strings (e.g. {@code "completed_with_errors"}). The two are NOT the same
 * casing convention; this file's `JOB_KIND_*` maps are keyed on the UPPER form the response
 * actually carries. The `?kind=` REQUEST query parameter, by contrast, is parsed server-side via
 * `JobKind.fromWire` and expects the LOWER form (see `toKindQueryParam` in `api/jobs.ts`).
 */
import type { StatusPillVariant } from '../components/StatusPill'
import { humanize } from '../lib/labels'

export type JobKind =
  | 'INGESTION_RUN'
  | 'BULK_UPDATE'
  | 'BULK_DELETE'
  | 'CAMPAIGN_GENERATE'
  | 'CAMPAIGN_SEND'
  | 'EXPORT'
  | 'BULK_AI_RUN'
  | 'DOSSIER_ASSEMBLY'
  | 'RECOMPUTE'
  | 'NOTIFICATION_DELIVERY'
  | 'NOTIFICATION_DIGEST'
  | 'FORM_RENDER'
  | 'BULK_PURGE'
  | 'OTHER'

export interface JobCounts {
  total: number
  processed: number
  succeeded: number
  failed: number
  skipped: number
}

export interface BackgroundJobView {
  id: string
  kind: JobKind
  sourceType: string
  sourceId: string
  initiatedBy: string
  status: string
  phase: string | null
  percent: number | null
  counts: JobCounts
  errorReportRef: string | null
  region: string
  startedAt: string | null
  completedAt: string | null
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
  version: number
}

export interface BackgroundJobDetailView {
  job: BackgroundJobView
  deepLinkUrl: string
}

/** `GET /background-jobs/{id}/error-report` response body (backend `internal.web.ErrorReportView`,
 * a signed URL via `SignedUrlStub`) — IB-27 S2 (Personal Jobs Tray, FR-M23.11). */
export interface ErrorReportView {
  url: string
}

/** Every `JobKind` -> a human label. Originally local to `DataImportScreen.tsx`'s "Recent
 * activity" panel; moved here (IB-27 S2 design §4) so the Jobs tray (`components/jobs/JobRow.tsx`)
 * and that screen share ONE map, never two drifting copies. */
export const KIND_LABEL: Record<JobKind, string> = {
  INGESTION_RUN: 'Ingestion run',
  BULK_UPDATE: 'Bulk update',
  BULK_DELETE: 'Bulk delete',
  BULK_PURGE: 'Bulk purge',
  BULK_AI_RUN: 'Bulk AI run',
  CAMPAIGN_GENERATE: 'Campaign generate',
  CAMPAIGN_SEND: 'Campaign send',
  EXPORT: 'Export',
  DOSSIER_ASSEMBLY: 'Dossier assembly',
  RECOMPUTE: 'Recompute',
  NOTIFICATION_DELIVERY: 'Notification delivery',
  NOTIFICATION_DIGEST: 'Notification digest',
  FORM_RENDER: 'Form render',
  OTHER: 'Other',
}

/** The general status -> `StatusPill` variant convention this screen/tray share (job lifecycle
 * statuses `queued|running|completed|completed_with_errors|failed|cancelled`, plus the plain
 * `active|disabled` values `DataImportScreen`'s Source-system/Pipeline panels also render through
 * the same `StatusPill`). Moved here alongside `KIND_LABEL` (IB-27 S2 design §4) — one map, no
 * drift between the Data screen and the Jobs tray. */
export const STATUS_VARIANT: Record<string, StatusPillVariant> = {
  active: 'success',
  disabled: 'muted',
  queued: 'neutral',
  running: 'info',
  completed: 'success',
  completed_with_errors: 'warning',
  failed: 'danger',
  cancelled: 'muted',
}

export function statusLabel(status: string): string {
  return humanize(status)
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}
